import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Bell, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const NotificationsAdmin = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendingOTD, setSendingOTD] = useState(false);
  const [todaysFact, setTodaysFact] = useState(null);
  const [otdScheduler, setOtdScheduler] = useState(null);
  const [togglingScheduler, setTogglingScheduler] = useState(false);
  const [notification, setNotification] = useState({ title: "", body: "", url: "/", subscribers_only: true });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, historyRes, factRes, schedulerRes] = await Promise.all([
        axios.get(`${API}/admin/push/stats`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/admin/push/history?limit=20`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/on-this-day`),
        axios.get(`${API}/on-this-day/scheduler/status`)
      ]);
      setStats(statsRes.data);
      setHistory(historyRes.data);
      setTodaysFact(factRes.data);
      setOtdScheduler(schedulerRes.data);
    } catch (error) {
      console.error("Failed to fetch push stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleOtdScheduler = async () => {
    setTogglingScheduler(true);
    try {
      const endpoint = otdScheduler?.scheduler_running 
        ? `${API}/on-this-day/scheduler/stop`
        : `${API}/on-this-day/scheduler/start`;
      
      await axios.post(endpoint, {}, { headers: { Authorization: `Bearer ${token}` }});
      toast.success(otdScheduler?.scheduler_running ? "Scheduler stopped" : "Scheduler started");
      fetchData();
    } catch (error) {
      toast.error("Failed to toggle scheduler");
    } finally {
      setTogglingScheduler(false);
    }
  };

  const sendNotification = async () => {
    if (!notification.title || !notification.body) {
      toast.error("Please enter title and message");
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/admin/push/send`, notification, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setNotification({ title: "", body: "", url: "/", subscribers_only: true });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send notification");
    } finally {
      setSending(false);
    }
  };

  const sendOnThisDayNotification = async () => {
    setSendingOTD(true);
    try {
      const response = await axios.post(`${API}/on-this-day/send-notification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("On This Day notification sent!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send On This Day notification");
    } finally {
      setSendingOTD(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#7C2C3B]" /></div>;
  }

  return (
    <div data-testid="admin-notifications">
      <h1 className="text-3xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
        <Bell className="w-8 h-8 inline mr-2 text-[#F8D742]" />
        PUSH NOTIFICATIONS
      </h1>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="text-3xl font-bold text-[#2BAEE5]">{stats?.active_subscriptions || 0}</div>
          <div className="text-sm text-slate-500">Active Subscribers</div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="text-3xl font-bold text-[#0F172A]">{stats?.total_subscriptions || 0}</div>
          <div className="text-sm text-slate-500">Total Subscriptions</div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="text-3xl font-bold text-green-600">{stats?.sent_notifications || 0}</div>
          <div className="text-sm text-slate-500">Sent</div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="text-3xl font-bold text-amber-600">{stats?.pending_notifications || 0}</div>
          <div className="text-sm text-slate-500">Scheduled</div>
        </div>
      </div>

      {/* VAPID Status */}
      <div className={`p-4 rounded-lg mb-6 ${stats?.vapid_configured ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${stats?.vapid_configured ? 'bg-green-500' : 'bg-amber-500'}`}></div>
          <span className={`text-sm font-medium ${stats?.vapid_configured ? 'text-green-700' : 'text-amber-700'}`}>
            {stats?.vapid_configured 
              ? "Push notifications are configured and ready" 
              : "VAPID keys not configured. Add keys to backend/.env to enable push notifications."}
          </span>
        </div>
      </div>

      {/* On This Day Quick Send */}
      {todaysFact && (
        <div className="bg-gradient-to-r from-[#7C2C3B] to-[#63232F] rounded-xl p-6 mb-8 text-white">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">📅</span>
                <h3 className="font-bold text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                  ON THIS DAY - {todaysFact.formatted_date}{todaysFact.year ? `, ${todaysFact.year}` : ''}
                </h3>
              </div>
              <h4 className="font-semibold text-[#F8D742] mb-2">{todaysFact.title}</h4>
              <p className="text-white/80 text-sm mb-4">{todaysFact.description}</p>
              
              {/* Scheduler Status */}
              <div className="bg-white/10 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${otdScheduler?.scheduler_running ? 'bg-green-400 animate-pulse' : 'bg-white/40'}`}></div>
                    <span className="text-sm font-medium">
                      Daily Auto-Notification: {otdScheduler?.scheduler_running ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <Button
                    onClick={toggleOtdScheduler}
                    disabled={togglingScheduler}
                    size="sm"
                    variant={otdScheduler?.scheduler_running ? "destructive" : "default"}
                    className={otdScheduler?.scheduler_running ? "bg-red-500 hover:bg-red-600" : "bg-green-500 hover:bg-green-600"}
                  >
                    {togglingScheduler ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : otdScheduler?.scheduler_running ? (
                      "Stop"
                    ) : (
                      "Start"
                    )}
                  </Button>
                </div>
                {otdScheduler?.scheduler_running && (
                  <div className="text-xs text-white/60 mt-2">
                    <p>⏰ Sends at 8:00 AM UK time daily</p>
                    {otdScheduler?.next_run && (
                      <p>Next: {new Date(otdScheduler.next_run).toLocaleString('en-GB', { timeZone: 'Europe/London' })}</p>
                    )}
                  </div>
                )}
              </div>
              
              <Button 
                onClick={sendOnThisDayNotification}
                disabled={sendingOTD || !stats?.vapid_configured}
                className="bg-[#F8D742] text-[#0F172A] hover:bg-yellow-300"
              >
                {sendingOTD ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> SENDING...</>
                ) : (
                  <><Bell className="w-4 h-4 mr-2" /> SEND NOW</>
                )}
              </Button>
            </div>
            <div className="text-4xl">⚽</div>
          </div>
        </div>
      )}

      {/* Send Notification Form */}
      <div className="bg-white rounded-xl p-6 border border-slate-200 mb-8">
        <h2 className="text-xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
          SEND NOTIFICATION
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Title</label>
            <Input 
              value={notification.title}
              onChange={(e) => setNotification({...notification, title: e.target.value})}
              placeholder="⚽ Match Day Alert!"
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Message</label>
            <Textarea 
              value={notification.body}
              onChange={(e) => setNotification({...notification, body: e.target.value})}
              placeholder="West Ham vs Arsenal kicks off in 1 hour!"
              rows={3}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Link URL (optional)</label>
            <Input 
              value={notification.url}
              onChange={(e) => setNotification({...notification, url: e.target.value})}
              placeholder="/fixtures"
              className="mt-1"
            />
          </div>
          <div className="flex items-center gap-2">
            <input 
              type="checkbox"
              checked={notification.subscribers_only}
              onChange={(e) => setNotification({...notification, subscribers_only: e.target.checked})}
              className="w-4 h-4"
            />
            <label className="text-sm text-slate-700">Send only to paying subscribers</label>
          </div>
          
          <Button 
            onClick={sendNotification}
            disabled={sending || !stats?.vapid_configured}
            className="w-full bg-[#7C2C3B] hover:bg-[#63232F]"
          >
            {sending ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> SENDING...</>
            ) : (
              <><Bell className="w-4 h-4 mr-2" /> SEND NOTIFICATION</>
            )}
          </Button>
        </div>
      </div>

      {/* Recent History */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h2 className="font-bold text-[#0F172A]">Recent Notifications</h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Message</TableHead>
              <TableHead>Sent</TableHead>
              <TableHead>Failed</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.length > 0 ? history.map((notif, idx) => (
              <TableRow key={idx}>
                <TableCell className="font-medium">{notif.title}</TableCell>
                <TableCell className="max-w-xs truncate text-sm text-slate-600">{notif.body}</TableCell>
                <TableCell className="text-green-600 font-bold">{notif.result?.sent || 0}</TableCell>
                <TableCell className="text-red-600">{notif.result?.failed || 0}</TableCell>
                <TableCell className="text-xs text-slate-500">{new Date(notif.created_at).toLocaleString()}</TableCell>
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                  No notifications sent yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default NotificationsAdmin;
